import { addMissingTranslation } from '@/services/ws-translate/src/main/resources/rest-api/index.ts';
import { BACKEND_NAMESPACE, parseTranslationKey } from './translation-key.js';
const TRANSLATION_KEY_PATTERN = /^[A-Z0-9-_]+$/;
const isResolutionDetails = (resolved) => typeof resolved === 'object' &&
    resolved !== null &&
    'usedLng' in resolved &&
    typeof resolved.usedLng === 'string' &&
    'usedNS' in resolved &&
    typeof resolved.usedNS === 'string';
/**
 * Creates an i18next post-processor that saves every translation key missing from its own namespace to ws-translate.
 *
 * A key is missing when its own namespace does not hold it, even if `fallbackNS` supplies the value on screen. Keys of
 * the `backend` namespace, keys without a namespace included, are never saved: they come from the backend. The
 * processor reads the resolving namespace from `i18nResolved`, which i18next passes only with `postProcessPassResolved`,
 * and saves each key once per page session.
 */
export const createMissingKeyRegistrar = (i18nInstance) => {
    const registeredKeys = new Set();
    return {
        type: 'postProcessor',
        name: 'missingKeyRegistrar',
        process(value, keys, options) {
            const requestedKey = (Array.isArray(keys) ? keys.at(-1) : keys) ?? '';
            const { namespace, key } = parseTranslationKey(requestedKey);
            const namespacedKey = `${namespace}:${key}`;
            const resolved = options.i18nResolved;
            if (namespace === BACKEND_NAMESPACE ||
                !TRANSLATION_KEY_PATTERN.test(key) ||
                registeredKeys.has(namespacedKey) ||
                !isResolutionDetails(resolved)) {
                return value;
            }
            // With `returnEmptyString: false` an empty value does not resolve a key. A plural lookup resolves a suffixed form,
            // so its base key may be absent from the namespace that resolved it.
            const isResolvedByOwnNamespace = typeof resolved.res === 'string' && resolved.res !== '' && resolved.usedNS === namespace;
            // A saved key keeps an empty value until it is translated.
            const isSaved = i18nInstance.getResource(resolved.usedLng, namespace, key) !== undefined;
            // A key counts as absent only once its own namespace is loaded for the language.
            if (isResolvedByOwnNamespace || isSaved || !i18nInstance.hasResourceBundle(resolved.usedLng, namespace)) {
                return value;
            }
            registeredKeys.add(namespacedKey);
            addMissingTranslation({ params: { translation: { namespace, key, pageRef: window.location.pathname } } }).catch(() => {
                // the key stays unregistered until the next page load retries it
            });
            return value;
        },
    };
};
